/* eslint-disable */

/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import './NightN.scss';
import { useCallback } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { useSessionStorageState, useTitle } from 'ahooks';
import { Alert, Button, Col, Radio, RadioChangeEvent, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import FetchingSpinner from 'components/fetching-spinner';
import NoData from 'components/no-data';
import useGetEdf from 'middleware/hcp/edf/useGetEdf';
import useGetFile from 'middleware/hcp/files/useGetFile';
import useGetRecording from 'middleware/hcp/recording/useGetRecording';
import useGetNightlyData from 'middleware/hcp/reports/useGetNightlyData';
import useGetNightlyReport from 'middleware/hcp/reports/useGetNightlyReport';
import useGetTestDetails from 'middleware/hcp/test/useGetTestDetails';
import { formatUDI } from 'utils/hcp/udi/udi.helper';
import { REPORT_TYPES, useAnalytics, useScreenTracking } from 'services/analytics';
import { Recording } from 'types/hcp/recording.type';

import UDI from 'assets/svgs/UDI.svg';

import DownloadOriginal from './DownloadOriginal';
import MeanAHI from './MeanAHI';
import NightError from './NightError';
import NoMeta from './NoMeta';
import Poincare from './Poincare';
import PositionStatistics from './PositionStatistics';
import PulseRate from './PulseRate';
import RecordingInfo from './RecordingInfo';
import SignalOverview from './SignalOverview';
import SPO from './SPO';
import TST from './TST';
import { setAPIServer } from 'utils/storage';

const NightN = ({ testId, recordingId }: { testId?: string; recordingId?: string }) => {
  useTitle('Reports - NightOwl');
  // GA Analytics: Log screen view
  useScreenTracking({ screenName: 'Night Report', screenClass: 'NightN' });
  const { t } = useTranslation();
  const { key, recording } = useParams();
  const [searchParams] = useSearchParams();
  const region = searchParams.get('region');
  if (region) {
    setAPIServer(region.toUpperCase());
  }
  const keyParam = key ?? testId;
  const recordingParam = recording ?? recordingId;
  const { logReportDownloaded } = useAnalytics();
  const { test } = useGetTestDetails(keyParam ?? '');
  const [score, setScore] = useSessionStorageState<string>('score', { defaultValue: '3' });
  const oldTest = test ? new Date(test.recent_recording_time) < new Date('2021-11-01') : false;
  const { nightly, nightlyLoading, nightlyError } = useGetNightlyData(
    recordingParam ?? '',
    oldTest
  );
  const { recording: recordingData } = useGetRecording(recordingParam ?? '');
  const { file, fileLoading } = useGetFile(nightly?.file ?? '');
  const { reportsList } = useGetNightlyReport(recordingParam ? [recordingParam] : []);
  const { downloadEdf, edfLoading } = useGetEdf(recordingData ? [recordingData._id] : []);

  const onChange = useCallback((e: RadioChangeEvent) => {
    setScore(e.target.value);
  }, []);

  const downloadEDF = async () => {
    // GA Analytics: Log EDF file download
    logReportDownloaded(REPORT_TYPES.EDF_FILE, keyParam);
    await downloadEdf();
  };

  if (oldTest || nightlyError?.message?.includes('500')) {
    return <NoMeta />;
  }
  if (nightlyError) {
    return <NoData type="info" message={t('no-data')} description={t('nightly.no-report')} />;
  }
  if (nightlyLoading || !nightly || edfLoading) {
    return <FetchingSpinner />;
  }

  const night = nightly?.night;

  // Normalize name parts, remove empty/whitespace-only values, and avoid dangling commas.
  const patientName =
    [test?.patient?.last_name, test?.patient?.first_name]
      .map((part) => (typeof part === 'string' ? part.trim() : part))
      .filter((part): part is string => typeof part === 'string' && part.length > 0)
      .join(', ') || undefined;

  if (nightly && (nightly?.type === 'ERROR' || nightly?.type === 'FAILURE')) {
    return (
      <div className="nightncontent">
        {recordingData && (
          <NightError
            code={nightly?.code}
            file={nightly.file}
            night={night}
            recording={recordingData}
            testId={keyParam}
          />
        )}
      </div>
    );
  }

  return (
    <div className="nightncontent">
      <Row className="max-width">
        <Col xs={{ span: 24 }} md={{ span: 16 }}>
          <div className="download-report-container" data-testid="night-n-download-container">
            {Array.isArray(reportsList) && reportsList?.[0]?.manual_edit && (
              <DownloadOriginal recordingId={recordingParam} />
            )}
            <a
              href={file}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                // GA Analytics: Log nightly report download
                logReportDownloaded(REPORT_TYPES.NIGHTLY_REPORT, keyParam);
              }}>
              <Button
                id="download-report"
                disabled={fileLoading}
                size="middle"
                icon={<DownloadOutlined />}
                type="primary"
                data-testid="night-n-download-btn">
                {t('download-report')}
              </Button>
            </a>
            {(recordingData as Recording)?.status === 'ANALYSED' && (
              <div onClick={downloadEDF} className="edf-download">
                <DownloadOutlined style={{ color: '#006DB7' }}></DownloadOutlined>
                <div className="link-text">{t('download-edf')}</div>
              </div>
            )}
          </div>
          {nightly?.variables?.TIT && nightly?.variables?.TIT < 4 && (
            <Alert
              style={{ marginTop: '15px' }}
              showIcon
              type="warning"
              message={t('nightly.caution')}
            />
          )}
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 8 }}>
          <div className="nightnflex" data-testid="night-n-score-selector">
            <span>{t('rule')}</span>
            <Radio.Group
              value={score ?? '3'}
              size="small"
              onChange={onChange}
              data-testid="night-n-score-toggle">
              <Radio.Button value="3" data-testid="night-n-score-3">
                3%
              </Radio.Button>
              <Radio.Button value="4" data-testid="night-n-score-4">
                4%
              </Radio.Button>
            </Radio.Group>
          </div>
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="main-row">
        <Col
          className="recording-info"
          xs={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 12 }}
          xl={{ span: 9 }}>
          <RecordingInfo nightly={nightly} />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 10 }}>
          <PulseRate nightly={nightly} />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 5 }}>
          <TST nightly={nightly} />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 9 }}>
          <SPO nightly={nightly} score={score ?? '3'} />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 20 }} xl={{ span: 15 }}>
          <MeanAHI nightly={nightly} score={score ?? '3'} />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
          <SignalOverview
            score={score ?? '3'}
            setScore={setScore}
            unitId={test?.unit?._id ?? ''}
            nightly={nightly}
            testId={keyParam}
            recordingId={recordingParam}
            patientName={patientName}
          />
        </Col>
        {nightly.variables.positionalReport && (
          <Col xs={{ span: 24 }} md={{ span: 18 }} lg={{ span: 12 }} xl={{ span: 12 }}>
            <PositionStatistics nightly={nightly} />
          </Col>
        )}
        {nightly?.settings?.showPoincare && (
          <Col xs={{ span: 24 }} md={{ span: 18 }} lg={{ span: 12 }} xl={{ span: 7 }}>
            <Poincare nightly={nightly} />
          </Col>
        )}
        {nightly?.variables?.UDI && (
          <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
            <div className="udi-container">
              <img src={UDI} alt="UDI" />
              <div className="udi-text">{formatUDI(nightly.variables.UDI)}</div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default NightN;
